// src/components/AddUserModal.tsx

import React, { useState, useEffect } from "react";
import "./AddUserModal.scss";

interface UserData {
  _id?: string;
  phoneNumber: string;
  name: string;
  email: string;
  gender: string;
  age: any;
  height: string;
  weight: string;
  goalWeightLoss: string;
  goalWeightGain: string;
  goalMuscleGain: string;
  goalHealthIssues: string;
  dietaryType: string;
  activity: string;
  medicalConcern: string;
  conditions: {
    diabetes: boolean;
    menopause: boolean;
    pcosPcod: boolean;
    thyroidism: boolean;
    cardiovascular: boolean;
    immunity: boolean;
    hairAndSkin: boolean;
    highUricAcid: boolean;
    anemia: boolean;
    ulcerativeColitis: boolean;
    crohnsDisease: boolean;
    sle: boolean;
    psoriasis: boolean;
    hashimotos: boolean;
    irritableBowelSyndrome: boolean;
    antiCancerDiets: boolean;
    otherMedicalConditions: boolean;
    otherMedicalConditionsDes: string;
  };
  languageCode: string;
  mealPreference: string[];
  stateName: string;
  cityName: string;
  profileRating: string;
}

interface AddUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
  userData?: UserData | null; // Optional prop for editing
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  onClose,
  onSuccess,
  userData,
}) => {
  const [formData, setFormData] = useState<UserData>({
    phoneNumber: "",
    name: "",
    email: "",
    gender: "",
    age: "",
    height: "",
    weight: "",
    goalWeightLoss: "",
    goalWeightGain: "",
    goalMuscleGain: "",
    goalHealthIssues: "",
    dietaryType: "",
    activity: "",
    medicalConcern: "No", // Defaulting to 'No'
    conditions: {
      diabetes: false,
      menopause: false,
      pcosPcod: false,
      thyroidism: false,
      cardiovascular: false,
      immunity: false,
      hairAndSkin: false,
      highUricAcid: false,
      anemia: false,
      ulcerativeColitis: false,
      crohnsDisease: false,
      sle: false,
      psoriasis: false,
      hashimotos: false,
      irritableBowelSyndrome: false,
      antiCancerDiets: false,
      otherMedicalConditions: false,
      otherMedicalConditionsDes: "",
    },
    languageCode: "",
    mealPreference: [],
    stateName: "",
    cityName: "",
    profileRating: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (userData) {
      setFormData({
        ...userData,
        age: userData.age || "",
        conditions: userData.conditions || {},
        goalWeightLoss: userData.goalWeightLoss || "",
        goalWeightGain: userData.goalWeightGain || "",
        goalMuscleGain: userData.goalMuscleGain || "",
        goalHealthIssues: userData.goalHealthIssues || "",
      });
    }
  }, [userData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const isChecked = (e.target as HTMLInputElement).checked;
      if (name.startsWith("goal")) {
        // Handle goal checkboxes, saving "true" or "false" as a string
        setFormData((prev) => ({
          ...prev,
          [name]: isChecked ? "true" : "false",
        }));
      } else {
        // Handle medical conditions checkboxes
        setFormData((prev) => ({
          ...prev,
          conditions: { ...prev.conditions, [name]: isChecked },
        }));
      }
    } else {
      // Handle other input types (text, email, number, select)
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleMealPreferenceChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const options = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    setFormData((prev) => ({
      ...prev,
      mealPreference: options,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const payload = {
      ...formData,
      age: formData.age ? Number(formData.age) : undefined,
    };

    try {
      const response = await fetch(
        "https://api.fitoja.in/api/save-personal-info-not-registered",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.msg);
        setTimeout(() => {
          onSuccess();
          onClose();
          window.location.reload();
        }, 500);
      } else {
        setError(data.msg || "Something went wrong!");
      }
    } catch (err) {
      setError("Failed to connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          &times;
        </button>
        <h2>{userData ? "Edit User Info" : "Add New User"}</h2>
        <form onSubmit={handleSubmit}>
          {/* Phone Number - Disabled for edit to prevent changing unique ID */}
          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="text"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              disabled={!!userData}
            />
          </div>

          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Gender</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Age</label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Height</label>
            <input
              type="text"
              name="height"
              value={formData.height}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Weight</label>
            <input
              type="text"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Goal</label>
            <div className="checkbox-container">
              <div
                className="checkbox-item"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    goalWeightLoss:
                      prev.goalWeightLoss === "true" ? "" : "true",
                  }))
                }
              >
                <input
                  type="checkbox"
                  id="goalWeightLoss"
                  name="goalWeightLoss"
                  checked={!!formData.goalWeightLoss}
                  onChange={handleChange}
                  value="Weight Loss"
                />
                <label htmlFor="goalWeightLoss">Weight Loss</label>
              </div>

              <div
                className="checkbox-item"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    goalWeightGain:
                      prev.goalWeightGain === "true" ? "" : "true",
                  }))
                }
              >
                <input
                  type="checkbox"
                  id="goalWeightGain"
                  name="goalWeightGain"
                  checked={!!formData.goalWeightGain}
                  onChange={handleChange}
                  value="Weight Gain"
                />
                <label htmlFor="goalWeightGain">Weight Gain</label>
              </div>

              <div
                className="checkbox-item"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    goalMuscleGain:
                      prev.goalMuscleGain === "true" ? "" : "true",
                  }))
                }
              >
                <input
                  type="checkbox"
                  id="goalMuscleGain"
                  name="goalMuscleGain"
                  checked={!!formData.goalMuscleGain}
                  onChange={handleChange}
                  value="Muscle Gain"
                />
                <label htmlFor="goalMuscleGain">Muscle Gain</label>
              </div>

              <div
                className="checkbox-item"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    goalHealthIssues:
                      prev.goalHealthIssues === "true" ? "" : "true",
                  }))
                }
              >
                <input
                  type="checkbox"
                  id="goalHealthIssues"
                  name="goalHealthIssues"
                  checked={!!formData.goalHealthIssues}
                  onChange={handleChange}
                  value="Health Issues"
                />
                <label htmlFor="goalHealthIssues">Health Issues</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Dietary Type</label>
            <select
              name="dietaryType"
              value={formData.dietaryType}
              onChange={handleChange}
            >
              <option>Select Dietary Type</option>
              <option value="Vegetarian">Vegetarian</option>
              <option value="Non-Vegetarian">Non-Vegetarian</option>
            </select>
          </div>

          <div className="form-group">
            <label>Activity</label>
            <select
              name="activity"
              value={formData.activity}
              onChange={handleChange}
            >
              <option value="">Select Activity Level</option>
              <option value="Sedentary">Sedentary</option>
              <option value="Lightly Active">Lightly Active</option>
              <option value="Moderately Active">Moderately Active</option>
              <option value="Very Active">Very Active</option>
              <option value="Extremely Active">Extremely Active</option>
            </select>
          </div>

          <div className="form-group">
            <label>Medical Concern</label>
            <select
              name="medicalConcern"
              value={formData.medicalConcern}
              onChange={handleChange}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>

          {formData.medicalConcern === "Yes" && (
            <div className="medical-conditions-group">
              <label>Medical Conditions</label>
              <div className="checkbox-container">
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="diabetes"
                    name="diabetes"
                    checked={formData.conditions.diabetes}
                    onChange={handleChange}
                  />
                  <label htmlFor="diabetes">Diabetes</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="menopause"
                    name="menopause"
                    checked={formData.conditions.menopause}
                    onChange={handleChange}
                  />
                  <label htmlFor="menopause">Menopause</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="pcosPcod"
                    name="pcosPcod"
                    checked={formData.conditions.pcosPcod}
                    onChange={handleChange}
                  />
                  <label htmlFor="pcosPcod">PCOS/PCOD</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="thyroidism"
                    name="thyroidism"
                    checked={formData.conditions.thyroidism}
                    onChange={handleChange}
                  />
                  <label htmlFor="thyroidism">Thyroidism</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="cardiovascular"
                    name="cardiovascular"
                    checked={formData.conditions.cardiovascular}
                    onChange={handleChange}
                  />
                  <label htmlFor="cardiovascular">Cardiovascular</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="immunity"
                    name="immunity"
                    checked={formData.conditions.immunity}
                    onChange={handleChange}
                  />
                  <label htmlFor="immunity">Immunity</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="hairAndSkin"
                    name="hairAndSkin"
                    checked={formData.conditions.hairAndSkin}
                    onChange={handleChange}
                  />
                  <label htmlFor="hairAndSkin">Hair and Skin</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="highUricAcid"
                    name="highUricAcid"
                    checked={formData.conditions.highUricAcid}
                    onChange={handleChange}
                  />
                  <label htmlFor="highUricAcid">High Uric Acid</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="anemia"
                    name="anemia"
                    checked={formData.conditions.anemia}
                    onChange={handleChange}
                  />
                  <label htmlFor="anemia">Anemia</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="ulcerativeColitis"
                    name="ulcerativeColitis"
                    checked={formData.conditions.ulcerativeColitis}
                    onChange={handleChange}
                  />
                  <label htmlFor="ulcerativeColitis">Ulcerative Colitis</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="crohnsDisease"
                    name="crohnsDisease"
                    checked={formData.conditions.crohnsDisease}
                    onChange={handleChange}
                  />
                  <label htmlFor="crohnsDisease">Crohn's Disease</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="sle"
                    name="sle"
                    checked={formData.conditions.sle}
                    onChange={handleChange}
                  />
                  <label htmlFor="sle">SLE</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="psoriasis"
                    name="psoriasis"
                    checked={formData.conditions.psoriasis}
                    onChange={handleChange}
                  />
                  <label htmlFor="psoriasis">Psoriasis</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="hashimotos"
                    name="hashimotos"
                    checked={formData.conditions.hashimotos}
                    onChange={handleChange}
                  />
                  <label htmlFor="hashimotos">Hashimoto's</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="irritableBowelSyndrome"
                    name="irritableBowelSyndrome"
                    checked={formData.conditions.irritableBowelSyndrome}
                    onChange={handleChange}
                  />
                  <label htmlFor="irritableBowelSyndrome">
                    Irritable Bowel Syndrome
                  </label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="antiCancerDiets"
                    name="antiCancerDiets"
                    checked={formData.conditions.antiCancerDiets}
                    onChange={handleChange}
                  />
                  <label htmlFor="antiCancerDiets">Anti-Cancer Diets</label>
                </div>

                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="otherMedicalConditions"
                    name="otherMedicalConditions"
                    checked={formData.conditions.otherMedicalConditions}
                    onChange={handleChange}
                  />
                  <label htmlFor="otherMedicalConditions">
                    Other Medical Conditions
                  </label>
                </div>
              </div>

              {formData.conditions.otherMedicalConditions && (
                <div className="form-group other-medical-des">
                  <label>Other Medical Conditions Description</label>
                  <input
                    type="text"
                    name="otherMedicalConditionsDes"
                    value={formData.conditions.otherMedicalConditionsDes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        conditions: {
                          ...prev.conditions,
                          otherMedicalConditionsDes: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Language Code</label>
            <input
              type="text"
              name="languageCode"
              value={formData.languageCode}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              name="stateName"
              value={formData.stateName}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              name="cityName"
              value={formData.cityName}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Profile Rating</label>
            <select
              name="profileRating"
              value={formData.profileRating}
              onChange={handleChange}
            >
              <option value="">Select Profile Rating</option>
              <option value="A">A</option>
              <option value="B">B</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Details"}
            </button>
          </div>
        </form>
        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message">{success}</p>}
      </div>
    </div>
  );
};

export default AddUserModal;
